import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { workQueueApi } from '@/api/workQueue.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageSection } from '@/components/ui/PageSection';
import { WorkQueueItemCard } from '@/components/workqueue/WorkQueueItemCard';
import { WorkQueuePriorityBadge } from '@/components/workqueue/WorkQueuePriorityBadge';
import type { WorkQueueListResponse } from '@/types/workQueue';
import { showApiError } from '@/utils/apiError';

export function ExecutiveWorkPage() {
  const [data, setData] = useState<WorkQueueListResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await workQueueApi.list({ scope: 'ALL', executive: true }));
    } catch (err) {
      showApiError(err, 'Failed to load executive queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const critical = (data?.items ?? []).filter((i) => i.priority === 'CRITICAL');

  return (
    <div className="page-content">
      <PageHeader
        title="Executive Command Center"
        description="Business-critical risks and alerts across revenue, operations, customers, and teams"
        breadcrumbs={[{ label: 'My Work', to: '/work' }, { label: 'Executive' }]}
      />

      <PageSection title="Critical risks" className="mb-6">
        {loading && <p className="text-sm text-slate-500">Loading…</p>}
        {!loading && (data?.executiveAlerts ?? []).length === 0 && critical.length === 0 && (
          <div className="card p-6 text-sm text-slate-500">No critical executive alerts right now.</div>
        )}
        <ul className="space-y-3">
          {(data?.executiveAlerts ?? []).map((a, i) => (
            <li key={i} className="card border-l-4 border-red-500 p-4">
              <div className="mb-1 flex items-center gap-2">
                <WorkQueuePriorityBadge priority={a.priority} />
                <span className="text-xs font-semibold uppercase text-slate-500">{a.category}</span>
              </div>
              <p className="font-semibold">{a.title}</p>
              {a.detail && <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{a.detail}</p>}
              {a.deepLink && (
                <Link to={a.deepLink} className="mt-2 inline-block text-sm text-primary-600 hover:underline">
                  Investigate →
                </Link>
              )}
            </li>
          ))}
        </ul>
      </PageSection>

      <PageSection title="Critical work queue">
        <div className="space-y-3">
          {critical.map((item) => (
            <WorkQueueItemCard key={item.id} item={item} onActionComplete={load} />
          ))}
        </div>
      </PageSection>
    </div>
  );
}
